import { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';

const COLORS = {
  gold: '#f0c040',
  cyan: '#00d4ff',
  green: '#00e87a',
  red: '#ff4444',
  bg: '#04060f',
  cardBg: '#0a1020',
  muted: '#4a5a7a',
};

const TOPPS_INDEX_URL = 'https://www.topps.com/pages/checklists';

export default function AdminChecklistsTopps({ user }) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sets, setSets] = useState([]);

  // UI state
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | added | not_added
  const [sortKey, setSortKey] = useState('year');
  const [sortDir, setSortDir] = useState('desc');
  const [updatingId, setUpdatingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Init: load role + sets
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    async function init() {
      setLoading(true);
      try {
        const { data: adminRow, error: roleErr } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (roleErr) throw roleErr;
        if (!mounted) return;

        if (!adminRow) {
          setRole(null);
          setLoading(false);
          return;
        }
        setRole(adminRow.role);

        const { data: setsData, error: setsErr } = await supabase
          .from('topps_sets')
          .select('*')
          .order('year', { ascending: false })
          .order('name', { ascending: true });

        if (setsErr) throw setsErr;
        if (!mounted) return;
        setSets(setsData || []);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Failed to load Topps tracker');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    init();
    return () => { mounted = false; };
  }, [user.id]);

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const goHome = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goAdmin = () => {
    window.history.pushState({}, '', '/admin/checklists');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  // ---------------------------------------------------------------------------
  // Toggle in_ck (owner only) — optimistic update with rollback on error
  // ---------------------------------------------------------------------------
  async function toggleInCk(setId, currentValue) {
    if (role !== 'owner') return;
    setUpdatingId(setId);

    setSets(prev => prev.map(s => s.id === setId ? { ...s, in_ck: !currentValue } : s));

    const { error: updateErr } = await supabase
      .from('topps_sets')
      .update({ in_ck: !currentValue })
      .eq('id', setId);

    if (updateErr) {
      setSets(prev => prev.map(s => s.id === setId ? { ...s, in_ck: currentValue } : s));
      alert('Update failed: ' + updateErr.message);
    }
    setUpdatingId(null);
  }

  // ---------------------------------------------------------------------------
  // Derived: filtered + sorted rows, added count
  // ---------------------------------------------------------------------------
  const filteredSorted = useMemo(() => {
    let rows = sets.slice();

    if (filterMode === 'added') rows = rows.filter(s => s.in_ck);
    else if (filterMode === 'not_added') rows = rows.filter(s => !s.in_ck);

    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        String(s.year).includes(q)
      );
    }

    rows.sort((a, b) => {
      let va, vb;
      switch (sortKey) {
        case 'year': va = a.year; vb = b.year; break;
        case 'name': va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); break;
        case 'release': va = a.release_date || ''; vb = b.release_date || ''; break;
        case 'cards': va = a.card_count || 0; vb = b.card_count || 0; break;
        case 'subsets': va = a.subset_count || 0; vb = b.subset_count || 0; break;
        default: va = 0; vb = 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });

    return rows;
  }, [sets, filterMode, search, sortKey, sortDir]);

  const addedCount = useMemo(() => sets.filter(s => s.in_ck).length, [sets]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(['year', 'release', 'cards', 'subsets'].includes(key) ? 'desc' : 'asc');
    }
  };

  const sortArrow = (key) => sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

  // ---------------------------------------------------------------------------
  // Render — loading / no-access / error / main
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ color: COLORS.muted, fontFamily: 'Share Tech Mono, monospace', fontSize: 13 }}>
          Loading Topps tracker...
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS · TOPPS</div>
          <div style={{ color: COLORS.red, fontFamily: 'Rajdhani, sans-serif', fontSize: 16, marginTop: 24 }}>
            You don't have admin access.
          </div>
          <div style={{ color: COLORS.muted, fontFamily: 'Rajdhani, sans-serif', fontSize: 14, marginTop: 8 }}>
            If you believe this is a mistake, contact the owner.
          </div>
          <button onClick={goHome} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO APP</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS · TOPPS</div>
          <div style={{ color: COLORS.red, fontFamily: 'Share Tech Mono, monospace', fontSize: 13, marginTop: 24, whiteSpace: 'pre-wrap' }}>
            Error: {error}
          </div>
          <button onClick={goAdmin} style={{ ...buttonOutlineStyle, marginTop: 20 }}>BACK TO CHECKLISTS</button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <div style={headerTitle}>ADMIN · CHECKLISTS · TOPPS</div>
          <div style={{
            fontFamily: 'Share Tech Mono, monospace',
            color: COLORS.green,
            fontSize: 11,
            letterSpacing: 2,
          }}>
            {(typeof window !== 'undefined' && window.location.hostname === 'collectionkeeper.org') ? 'PRODUCTION' : 'STAGING'} · {role.toUpperCase()}
          </div>
        </div>

        <div style={{
          fontFamily: 'Rajdhani, sans-serif',
          color: COLORS.muted,
          fontSize: 14,
          marginBottom: 24,
        }}>
          Track which Topps Star Wars sets are in CollectionKeeper. Source:{' '}
          <a href={TOPPS_INDEX_URL} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.cyan, textDecoration: 'none' }}>
            topps.com/pages/checklists
          </a>
        </div>

        {/* Top actions */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={goAdmin} style={buttonOutlineStyle}>← BACK TO CHECKLISTS</button>
          <button onClick={goHome} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}>BACK TO APP</button>
          <div style={{ flex: 1 }} />
          <button onClick={signOut} style={{ ...buttonOutlineStyle, color: COLORS.muted, borderColor: COLORS.muted }}>
            SIGN OUT
          </button>
        </div>

        {/* Stat + search + filter */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={statPillStyle}>
            <span style={{ color: COLORS.green, fontWeight: 700, fontSize: 16 }}>{addedCount}</span>
            <span style={{ color: COLORS.muted }}> / {sets.length} added</span>
          </div>

          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sets..."
            style={searchInputStyle}
          />

          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            style={selectStyle}
          >
            <option value="all">All sets</option>
            <option value="not_added">Not in CK</option>
            <option value="added">In CK</option>
          </select>
        </div>

        {/* Table */}
        <div style={{
          background: COLORS.cardBg,
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${COLORS.muted}22`,
          overflowX: 'auto',
        }}>
          {/* Header row */}
          <div style={tableHeaderRow}>
            <div style={sortableHeaderStyle} onClick={() => handleSort('year')}>YEAR{sortArrow('year')}</div>
            <div style={sortableHeaderStyle} onClick={() => handleSort('name')}>SET NAME{sortArrow('name')}</div>
            <div style={sortableHeaderStyle} onClick={() => handleSort('release')}>RELEASE{sortArrow('release')}</div>
            <div style={sortableHeaderStyle} onClick={() => handleSort('cards')}>CARDS{sortArrow('cards')}</div>
            <div style={sortableHeaderStyle} onClick={() => handleSort('subsets')}>SUB-SETS{sortArrow('subsets')}</div>
            <div>CHECKLIST</div>
            <div>IN CK</div>
          </div>

          {filteredSorted.length === 0 ? (
            <div style={{
              padding: '40px 16px',
              textAlign: 'center',
              fontFamily: 'Rajdhani, sans-serif',
              color: COLORS.muted,
              fontSize: 14,
            }}>
              No sets match your filter.
            </div>
          ) : (
            filteredSorted.map(s => (
              <div key={s.id} style={{
                ...tableDataRow,
                background: s.in_ck ? `${COLORS.green}0d` : 'transparent',
                boxShadow: s.in_ck ? `inset 3px 0 0 ${COLORS.green}` : 'none',
              }}>
                <div style={{ fontFamily: 'Share Tech Mono, monospace', color: COLORS.cyan, fontWeight: 600 }}>
                  {s.year}
                </div>
                <div style={{ color: s.in_ck ? COLORS.green : '#fff', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.name}
                </div>
                <div style={mutedMonoCell}>{s.release_date || '—'}</div>
                <div style={mutedMonoCell}>{s.card_count != null ? s.card_count : '—'}</div>
                <div style={mutedMonoCell}>{s.subset_count != null ? s.subset_count : '—'}</div>
                <div>
                  {s.pdf_url ? (
                    <a href={s.pdf_url} target="_blank" rel="noopener noreferrer" style={dlBtnStyle}>
                      ↓ PDF
                    </a>
                  ) : (
                    <span style={{ ...dlBtnStyle, opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' }}>—</span>
                  )}
                </div>
                <div>
                  <button
                    onClick={() => toggleInCk(s.id, s.in_ck)}
                    disabled={role !== 'owner' || updatingId === s.id}
                    style={{
                      ...toggleBtnStyle,
                      background: s.in_ck ? `${COLORS.green}26` : 'transparent',
                      borderColor: s.in_ck ? COLORS.green : `${COLORS.muted}55`,
                      color: s.in_ck ? COLORS.green : COLORS.muted,
                      opacity: updatingId === s.id ? 0.5 : 1,
                      cursor: (role !== 'owner' || updatingId === s.id) ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: s.in_ck ? COLORS.green : 'transparent',
                      border: `2px solid ${s.in_ck ? COLORS.green : COLORS.muted}`,
                      marginRight: 6,
                      verticalAlign: 'middle',
                    }} />
                    {s.in_ck ? 'ADDED' : 'NOT ADDED'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 24,
          padding: 12,
          background: COLORS.cardBg,
          borderRadius: 4,
          fontFamily: 'Share Tech Mono, monospace',
          color: COLORS.muted,
          fontSize: 11,
          lineHeight: 1.6,
        }}>
          Phase 1: 62 Star Wars sets seeded from topps.com (2018+ only). Daily auto-refresh and email notifications coming in Phase 2.
          {role === 'super_user' && <div style={{ marginTop: 6 }}>Super users can view but only owners can toggle In CK status.</div>}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared styles — matches AdminChecklists.js conventions
// -----------------------------------------------------------------------------

const containerStyle = {
  minHeight: '100vh',
  background: COLORS.bg,
  padding: '24px 16px',
  fontFamily: 'Rajdhani, sans-serif',
  color: '#fff',
};

const headerTitle = {
  fontFamily: 'Orbitron, sans-serif',
  color: COLORS.gold,
  fontSize: 20,
  letterSpacing: 1,
};

const buttonOutlineStyle = {
  padding: '10px 18px',
  background: 'transparent',
  color: COLORS.cyan,
  border: `1px solid ${COLORS.cyan}`,
  borderRadius: 4,
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 12,
  letterSpacing: 1.5,
  cursor: 'pointer',
};

const statPillStyle = {
  background: COLORS.cardBg,
  border: `1px solid ${COLORS.muted}33`,
  borderRadius: 6,
  padding: '10px 14px',
  fontFamily: 'Share Tech Mono, monospace',
  fontSize: 13,
  color: '#fff',
};

const searchInputStyle = {
  flex: 1,
  minWidth: 200,
  background: COLORS.cardBg,
  border: `1px solid ${COLORS.muted}33`,
  color: '#fff',
  padding: '10px 14px',
  fontFamily: 'Rajdhani, sans-serif',
  fontSize: 14,
  borderRadius: 6,
  outline: 'none',
};

const selectStyle = {
  background: COLORS.cardBg,
  border: `1px solid ${COLORS.muted}33`,
  color: '#fff',
  padding: '10px 14px',
  fontFamily: 'Rajdhani, sans-serif',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 6,
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const tableHeaderRow = {
  display: 'grid',
  gridTemplateColumns: '70px 2fr 110px 70px 90px 100px 130px',
  padding: '10px 16px',
  background: COLORS.bg,
  borderBottom: `1px solid ${COLORS.muted}22`,
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 11,
  color: COLORS.gold,
  letterSpacing: 1,
  alignItems: 'center',
  gap: 8,
  minWidth: 900,
};

const tableDataRow = {
  display: 'grid',
  gridTemplateColumns: '70px 2fr 110px 70px 90px 100px 130px',
  padding: '12px 16px',
  borderBottom: `1px solid ${COLORS.muted}22`,
  fontFamily: 'Rajdhani, sans-serif',
  fontSize: 14,
  color: '#fff',
  alignItems: 'center',
  gap: 8,
  minWidth: 900,
};

const sortableHeaderStyle = {
  cursor: 'pointer',
  userSelect: 'none',
};

const mutedMonoCell = {
  fontFamily: 'Share Tech Mono, monospace',
  color: COLORS.muted,
  fontSize: 12,
};

const dlBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  borderRadius: 4,
  border: `1px solid ${COLORS.cyan}55`,
  background: `${COLORS.cyan}10`,
  color: COLORS.cyan,
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.2,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const toggleBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 11px',
  borderRadius: 999,
  border: '1px solid',
  fontFamily: 'Orbitron, sans-serif',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.2,
  whiteSpace: 'nowrap',
};
